import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, BellRing, Loader2, Check, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TempOutage = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");
  const [purpose, setPurpose] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !duration.trim() || !purpose || !email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const { error: dbError } = await supabase
        .from("outage_notify_requests")
        .insert({
          name,
          email,
          duration,
          purpose,
          user_agent: navigator.userAgent,
        });
      if (dbError) throw dbError;

      const esc = (s: string) =>
        s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const html = `
        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:600px;margin:0 auto;padding:24px;background:#f9f8f5;">
          <div style="background:#fff;border-radius:8px;padding:28px;">
            <h2 style="color:#1f3d32;margin:0 0 16px 0;">New outage notify request</h2>
            <p style="margin:6px 0;"><strong>Name:</strong> ${esc(name)}</p>
            <p style="margin:6px 0;"><strong>Email:</strong> ${esc(email)}</p>
            <p style="margin:6px 0;"><strong>Duration wanted:</strong> ${esc(duration)}</p>
            <p style="margin:6px 0;"><strong>Purpose:</strong> ${esc(purpose)}</p>
            <hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;">
            <p style="color:#6b7280;font-size:12px;margin:0;">Submitted from /temp-outage</p>
          </div>
        </div>`;

      const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
          to: "zain@qwip.co.uk",
          subject: `Outage notify request from ${name}`,
          type: "custom",
          html,
          replyTo: email,
        },
      });
      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Failed to send");
      }
      setSent(true);
      toast.success("Thanks — we'll be in touch with your discount soon");
    } catch (err) {
      console.error("Outage form error:", err);
      toast.error("Couldn't submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    if (loading) return;
    setOpen(false);
    if (sent) {
      setTimeout(() => {
        setSent(false);
        setName("");
        setDuration("");
        setPurpose("");
        setEmail("");
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 pt-32 pb-20 px-6">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-card border border-border rounded-2xl p-8 md:p-10 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[hsl(13,70%,63%)]/15 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-[hsl(13,70%,63%)]" />
            </div>

            <h1 className="text-3xl md:text-4xl font-funnel font-semibold text-primary mb-3">
              This booth is out of service
            </h1>
            <p className="text-foreground/70 leading-relaxed mb-2">
              We're facing a temporary power outage, so this pod isn't bookable right now.
            </p>
            <p className="text-foreground/70 leading-relaxed mb-4">
              We're sorry for any inconvenience caused. Feel free to enter your details below to be notified when the pod is back up and running soon.
            </p>
            <p className="text-foreground/70 leading-relaxed mb-8">
              We'll also give you a discount on your next booking.
            </p>

            <Button
              size="lg"
              onClick={() => setOpen(true)}
              className="w-full rounded-full"
            >
              <BellRing className="w-4 h-4" />
              Get notified
            </Button>
          </motion.div>
        </div>
      </main>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
            onClick={closeModal}
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-background rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 pt-5 pb-2">
                <h2 className="font-funnel text-xl font-semibold text-primary">
                  {sent ? "You're on the list" : "Get notified"}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 -mr-2 rounded-full hover:bg-muted transition-colors"
                  aria-label="close"
                >
                  <X className="w-5 h-5 text-foreground/60" />
                </button>
              </div>

              {sent ? (
                <div className="px-6 pb-8 pt-2 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-7 h-7 text-primary" />
                  </div>
                  <p className="text-foreground/70 mb-6">
                    Thanks - we'll email you as soon as the booth is back, along with a discount for your next session.
                  </p>
                  <Button onClick={closeModal} className="w-full rounded-full" size="lg">
                    Done
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="px-6 pb-6 pt-2 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                      Your name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      disabled={loading}
                      maxLength={100}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="Jane Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                      How long did you want the booth for?
                    </label>
                    <input
                      type="text"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      disabled={loading}
                      maxLength={60}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="e.g. 30 minutes, 1 hour"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                      What for?
                    </label>
                    <select
                      value={purpose}
                      onChange={(e) => setPurpose(e.target.value)}
                      disabled={loading}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                    >
                      <option value="">Select one</option>
                      <option value="Work">Work</option>
                      <option value="Study">Study</option>
                      <option value="Call">Call</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground/80 mb-1.5">
                      Your email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      maxLength={255}
                      className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                      placeholder="you@example.com"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    size="lg"
                    className="w-full rounded-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <BellRing className="w-4 h-4" />
                        Get notified
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default TempOutage;
